import { Button, Group, Stack } from '@mantine/core';
import { FileRejection } from '@mantine/dropzone';
import { useForm } from '@mantine/form';

import { MAX_CONTROL_DOCUMENT_SIZE } from '@e-origin/shared';

import { MultiLangCodes, TextInput, UploadFile } from '@components';
import { useUploadProvidedDocument } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';

interface ProvidedDocumentFormValues {
  type: string;
  referenceNumber: string;
  description: string;
  file: File | null;
}

const MAX_CONTROL_DOCUMENT_SIZE_MB = MAX_CONTROL_DOCUMENT_SIZE / (1024 * 1024);

interface AddProvidedDocumentProps {
  declarationId: string;
  onHide: () => void;
}

export const AddProvidedDocument = ({ declarationId, onHide }: AddProvidedDocumentProps) => {
  const uploadMutation = useUploadProvidedDocument();
  const codelistSystems = useCodeListSystem({});

  const form = useForm<ProvidedDocumentFormValues>({
    initialValues: {
      type: '',
      referenceNumber: '',
      description: '',
      file: null,
    },
    validate: {
      file: (value) => (!value ? 'File is required' : null),
    },
  });

  const handleFileReject = (fileRejections: FileRejection[]) => {
    const isTooLarge = fileRejections[0]?.errors?.some((error) => error.code === 'file-too-large');
    const reason = isTooLarge ? `File is larger than ${MAX_CONTROL_DOCUMENT_SIZE_MB} MB` : 'File type is not supported';
    const previousFile = form.values.file ? ` "${form.values.file.name}" is still selected.` : '';

    form.setFieldError('file', `${reason} and was not attached.${previousFile}`);
  };

  const handleSubmit = async (values: ProvidedDocumentFormValues) => {
    if (!values.file) return;
    await uploadMutation.mutateAsync({
      declarationId,
      file: values.file,
      documentData: {
        type: values.type,
        referenceNumber: values.referenceNumber,
        description: values.description,
      },
    });
    onHide();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <MultiLangCodes {...form.getInputProps('type')} label="Type" code={['CL754', 'CL213']} system={codelistSystems} required />
        <TextInput {...form.getInputProps('referenceNumber')} label="Reference" required />
        <TextInput {...form.getInputProps('description')} label="Description" required />
        <UploadFile {...form.getInputProps('file')} isMulti={false} type="documents" maxSize={MAX_CONTROL_DOCUMENT_SIZE} onReject={handleFileReject} p="xs" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit">Add</Button>
        </Group>
      </Stack>
    </form>
  );
};
